import { z } from 'zod';

const objectForSelection = {
  id: z.number(),
  label: z.string()
};

interface IDefaultOperationsSchema {
  id: string | number;
  label?: string;
}

const DefaultOperationsSchema: z.ZodSchema<IDefaultOperationsSchema> = z.lazy(
  () =>
    z.object({
      id: z.string().or(z.number()),
      label: z.string().optional()
    })
);

const RequiredFieldsSchema = z.object({
  name: z.boolean(),
  asset: z.boolean(),
  department: z.boolean(),
  employees: z.boolean(),
  plannedStartDate: z.boolean(),
  actualStartDate: z.boolean(),
  plannedEndDate: z.boolean(),
  actualEndDate: z.boolean(),
  description: z.boolean(),
  attachment: z.boolean(),
  plannedOperations: z.boolean(),
  actualOperations: z.boolean()
});

const timeFormatRegex = /^([0-9]{1,4}):([0-5][0-9])$/;
const maxAllowedTimeInMinutes = 999 * 60 + 59;

const createTaskTypeTimeSchema = (
  requiredMessage: string,
  formatMessage: string
) =>
  z
    .string()
    .nonempty({ message: requiredMessage })
    .regex(timeFormatRegex, {
      message: formatMessage
    })
    .refine(
      (value) => {
        const [hoursString, minutesString] = value.split(':');
        const hours = Number(hoursString);
        const minutes = Number(minutesString);

        if (Number.isNaN(hours) || Number.isNaN(minutes)) {
          return false;
        }

        return hours * 60 + minutes <= maxAllowedTimeInMinutes;
      },
      {
        message: 'Значение не может быть больше 999:59'
      }
    );

export const TaskTypeSchema = z.object({
  id: z.number().or(z.string()),
  planningAlgorithm: z.object(objectForSelection).optional(),
  taskType: z
    .string()
    .nonempty({ message: 'Пожалуйста, укажите название типа наряда' })
    .refine((value) => value.trim().length > 0, {
      message: 'Поле не может содержать только пробелы'
    }),
  statusTransition: z.object(objectForSelection).optional(),
  processingTime: z
    .preprocess(
      (value) =>
        value === null || value === undefined || value === ''
          ? undefined
          : value,
      z.any()
    )
    .superRefine((value, ctx) => {
      if (value === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Пожалуйста, укажите плановые трудозатраты'
        });
        return;
      }

      const isString = typeof value === 'string';
      const isNumber = typeof value === 'number';
      if (!isString && !isNumber) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Пожалуйста, укажите плановые трудозатраты'
        });
        return;
      }

      const parsed = Number(value);
      if (parsed < 0 || parsed > 1000001) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Допустимые значения: от 0 до 1000000'
        });
      }
    })
    .transform((value) => Number(value)),
  defaultOperations: z.array(DefaultOperationsSchema).optional(),
  priority: z.number().or(z.string()).optional(),
  isCustomEffort: z.boolean().optional(),
  requiredFields: RequiredFieldsSchema,
  inventory: z.boolean(),
  assignDepartmentOnSave: z.boolean(),
  responseTime: createTaskTypeTimeSchema(
    'Пожалуйста, укажите плановый срок начала',
    'Формат должен быть чч:мм или ччч:мм (например, 01:30)'
  ),
  resolutionTime: createTaskTypeTimeSchema(
    'Пожалуйста, укажите время решения',
    'Формат должен быть чч:мм или ччч:мм (например, 02:00)'
  ),
  requiredEmployees: z
    .number({ invalid_type_error: 'Укажите количество исполнителей' })
    .int({ message: 'Значение должно быть целым числом' })
    .min(0, { message: 'Минимальное значение: 0' })
    .max(1000, { message: 'Максимальное значение: 1000' })
});
